import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn';

export interface DropdownItem {
  key: string;
  label: ReactNode;
  onSelect?: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  /** Renders in the danger tone and after a divider. For destructive actions. */
  destructive?: boolean;
  /** Draws a divider above this item. */
  separatorBefore?: boolean;
}

export interface DropdownProps {
  /** The button that opens the menu. Receives the open state through render props. */
  trigger: (props: { open: boolean }) => ReactNode;
  items: DropdownItem[];
  align?: 'start' | 'end';
  /** Accessible name for the menu itself, e.g. "Case actions". */
  label: string;
  className?: string;
  menuClassName?: string;
}

/**
 * An actions menu.
 *
 * Keyboard behaviour follows the menu-button pattern: Enter/Space/ArrowDown opens
 * and focuses the first item, ArrowUp opens at the last, arrows move, Home/End jump,
 * Escape closes and returns focus to the trigger, Tab closes.
 *
 * Only put ACTIONS in here. Navigation belongs in a nav element, and a menu of links
 * announced as a menu is confusing to anyone using a screen reader.
 */
export function Dropdown({
  trigger,
  items,
  align = 'end',
  label,
  className,
  menuClassName,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const enabledIndexes = items
    .map((item, index) => (item.disabled ? -1 : index))
    .filter((index) => index >= 0);

  const close = useCallback(
    (returnFocus = true) => {
      setOpen(false);
      setActiveIndex(-1);
      if (returnFocus) triggerRef.current?.focus();
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, close]);

  useEffect(() => {
    if (open && activeIndex >= 0) itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  const openAt = (position: 'first' | 'last') => {
    setOpen(true);
    const index =
      position === 'first' ? enabledIndexes[0] : enabledIndexes[enabledIndexes.length - 1];
    setActiveIndex(index ?? -1);
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openAt('first');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAt('last');
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const position = enabledIndexes.indexOf(activeIndex);
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        setActiveIndex(enabledIndexes[(position + 1) % enabledIndexes.length] ?? -1);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const next = position <= 0 ? enabledIndexes.length - 1 : position - 1;
        setActiveIndex(enabledIndexes[next] ?? -1);
        break;
      }
      case 'Home':
        event.preventDefault();
        setActiveIndex(enabledIndexes[0] ?? -1);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(enabledIndexes[enabledIndexes.length - 1] ?? -1);
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        close(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : openAt('first'))}
        onKeyDown={onTriggerKeyDown}
        className="inline-flex"
      >
        {trigger({ open })}
      </button>

      {open && (
        <div
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          className={cn(
            'absolute z-40 mt-1 min-w-[12rem] animate-scale-in rounded-lg border',
            'bg-surface-raised py-1 shadow-overlay',
            align === 'end' ? 'right-0' : 'left-0',
            menuClassName,
          )}
        >
          {items.map((item, index) => (
            <div key={item.key}>
              {item.separatorBefore && <div className="my-1 border-t" role="separator" />}
              <button
                ref={(element) => {
                  itemRefs.current[index] = element;
                }}
                type="button"
                role="menuitem"
                tabIndex={activeIndex === index ? 0 : -1}
                disabled={item.disabled}
                onClick={() => {
                  item.onSelect?.();
                  close();
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-base transition-colors',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  item.destructive
                    ? 'text-danger hover:bg-danger-subtle'
                    : 'text-ink hover:bg-surface-sunken',
                )}
              >
                {item.icon && <span aria-hidden="true">{item.icon}</span>}
                {item.label}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
